import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-md border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap shadow-sm transition-all outline-none select-none focus-visible:outline-none active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-4 aria-invalid:ring-destructive/10 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary-dark focus-visible:ring-4 focus-visible:ring-primary/20",
        outline:
          "border-subtle border bg-card text-default hover:bg-[color-mix(in_srgb,var(--color-surface-subtle)_50%,transparent)] hover:text-default focus-visible:ring-4 focus-visible:ring-[color-mix(in_srgb,var(--color-border-subtle)_50%,transparent)]",
        secondary:
          "border-subtle border bg-card text-default hover:bg-[color-mix(in_srgb,var(--color-surface-subtle)_50%,transparent)] focus-visible:ring-4 focus-visible:ring-[color-mix(in_srgb,var(--color-border-subtle)_50%,transparent)]",
        ghost:
          "shadow-none hover:bg-[color-mix(in_srgb,var(--color-surface-subtle)_50%,transparent)] hover:text-default",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-4 focus-visible:ring-destructive/20",
        success:
          "bg-success text-white hover:bg-success/90 focus-visible:ring-4 focus-visible:ring-success/20",
        danger:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-4 focus-visible:ring-destructive/20",
        link: "shadow-none text-primary underline-offset-4 hover:underline active:scale-100",
      },
      size: {
        default: "h-9 px-4 py-2 gap-2 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3",
        xs: "h-8 px-2.5 text-xs gap-1",
        sm: "h-9 px-3 text-xs gap-1.5 has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5",
        lg: "h-10 px-6 text-sm gap-2 has-data-[icon=inline-end]:pr-4 has-data-[icon=inline-start]:pl-4",
        icon: "size-9",
        "icon-xs": "size-8 rounded-md",
        "icon-sm": "size-9 rounded-md",
        "icon-lg": "size-10 rounded-md",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean
    }
>(({ className, variant = "default", size = "default", asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      ref={ref}
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
})
Button.displayName = "Button"

export { Button, buttonVariants }
